import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { type CSSProperties, type KeyboardEvent, useCallback, useMemo } from 'react'

import { hasMenuContent } from '@src/components/widgets/navigation/utils/has-menu-content'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Carousel, CarouselContent, CarouselItem } from '@src/ds/blocks/ds-carousel'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'

import { NavItem } from './nav-item'
import { type NavCategory } from './types'

const bandClass = css({
  height: { base: '57px', lg: '65px' },
  bg: 'white',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'lightgray',
  // base 0 is load-bearing — it out-ranks the outer band's base gutter so the pill carousel
  // stays full-bleed on mobile (the track's own padding is the sole leading gutter).
  paddingInline: { base: 0, lg: 'space-48' },
})

// Legacy's white content mode: every row item goes white over a dark hero, highlight included.
// Match NavItem's `:focus-visible` specificity — a bare `[data-nav-label]` loses to the control's black ring.
const onDarkBandClass = css({
  '& a, & button': { color: 'white' },
  '& :is(a, button):focus-visible [data-nav-label]': { outlineColor: 'white' },
})

// The clip edge moves 8px into the gutter (so the first item's focus ring shows) but content stays put.
const carouselClass = css.raw({
  flex: '1 0 0',
  minWidth: 0,
  height: '100%',
  marginInlineStart: { lg: '-8px' },
  width: { lg: 'calc(100% + 8px)' },
})

// Adding extra space around item for the focus outline, otherwise it gets clipped.
const viewportClass = css.raw({
  height: '100%',
  paddingBlock: 'space-8',
  marginBlock: '-8px',
})

const EDGE_INSET = { base: 'space-16', lg: 'space-48' } as const

const trackClass = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: 'space-24',
  height: '100%',
  paddingInlineStart: { base: 'space-16', lg: 'space-8' },
})

const itemClass = css.raw({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
})

// Trailing gutter on the last slide, not the track: `trimSnaps` clips a track
// padding-inline-end that sits behind the overflowing items.
const lastItemClass = css.raw({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
  paddingInlineEnd: EDGE_INSET,
})

export const buildMenuPanelId = (categoryId: string) => `nav-menu-${categoryId}`

interface NavCategoryRowProps {
  categories: NavCategory[]
  activeCategoryId?: string | null
  onCategoryEnter?: (id: string) => void
  onCategoryLeave?: () => void
  onCategoryFocus?: (id: string) => void
  /** Fires when a plain navigating link in the row is clicked (never for a menu trigger). */
  onCategoryLinkClick?: (category: NavCategory, index: number) => void
  /** Moves focus into the open menu panel; returns whether it moved. */
  onDiveIntoPanel?: () => boolean
  triggerRef?: React.MutableRefObject<HTMLElement | null>
  isHidden?: boolean
  /** Drops the band's own paint so a transparent header shows the hero through it. */
  isTransparent?: boolean
  /** Paints the row white, for a transparent header authored with white content. */
  isOnDark?: boolean
  /**
   * On mobile, categories with menus render as plain navigating links (mobile has
   * no hover, so the mega menu can never open from this row). On desktop, mega-menu
   * categories stay hover-only triggers.
   */
  isMobile?: boolean
}

export const NavCategoryRow = ({
  categories,
  activeCategoryId,
  onCategoryEnter,
  onCategoryLeave,
  onCategoryFocus,
  onCategoryLinkClick,
  onDiveIntoPanel,
  triggerRef,
  isHidden = false,
  isTransparent = false,
  isOnDark = false,
  isMobile = false,
}: NavCategoryRowProps) => {
  const { t } = useTranslation('common')
  const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])

  // Pointer/focus on a category with a menu opens it; on a plain-link category, the
  // same event is treated as a hover-out and starts the delayed close of any open menu.
  const handlePointerEnter = useCallback(
    (id: string) => {
      const category = categoriesById.get(id)
      if (!category || !hasMenuContent(category)) {
        onCategoryLeave?.()
        return
      }
      onCategoryEnter?.(id)
    },
    [categoriesById, onCategoryEnter, onCategoryLeave],
  )

  const handleFocus = useCallback(
    (id: string) => {
      const category = categoriesById.get(id)
      if (!category || !hasMenuContent(category)) {
        onCategoryLeave?.()
        return
      }
      ;(onCategoryFocus ?? onCategoryEnter)?.(id)
    },
    [categoriesById, onCategoryEnter, onCategoryFocus, onCategoryLeave],
  )

  // Enter/Space/ArrowDown on an open trigger dives into its panel; on a closed
  // one (e.g. after Escape) it reopens the panel without diving.
  const handleKeyDown = useCallback(
    (id: string, event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown') {
        return
      }
      if (activeCategoryId === id) {
        if (onDiveIntoPanel?.()) {
          event.preventDefault()
        }
        return
      }
      ;(onCategoryFocus ?? onCategoryEnter)?.(id)
      event.preventDefault()
    },
    [activeCategoryId, onDiveIntoPanel, onCategoryFocus, onCategoryEnter],
  )

  const bandStyle = useMemo<CSSProperties | undefined>(() => {
    if (!isTransparent && !isHidden) {
      return undefined
    }
    return {
      // The hairline stays: legacy keeps a 10%-white rule between the rows of a transparent header.
      ...(isTransparent && { backgroundColor: 'transparent', borderTopColor: 'rgba(255, 255, 255, 0.1)' }),
      ...(isHidden && { visibility: 'hidden' }),
    }
  }, [isHidden, isTransparent])

  const setTriggerEl = useCallback(
    (el: HTMLElement | null) => {
      if (triggerRef) {
        triggerRef.current = el
      }
    },
    [triggerRef],
  )

  return (
    <SectionContentContainer
      // Header chrome: the white band + top rule stay full bleed at every width
      // (STRFRNT-16552 excludes header and footer from the background cap).
      fullBleed
      className={cx(bandClass, isOnDark && onDarkBandClass)}
      onPointerLeave={onCategoryLeave}
      aria-hidden={isHidden || undefined}
      style={bandStyle}
    >
      <Carousel
        aria-label={t('categories')}
        data-testid={TEST_LOCATORS.NAV.NAV_CATEGORY_ROW}
        opts={{ align: 'start', containScroll: 'trimSnaps', dragFree: true }}
        css={carouselClass}
      >
        <CarouselContent css={trackClass} viewportCss={viewportClass}>
          {categories.map((category, index) => {
            const isActive = activeCategoryId === category.id
            const isLast = index === categories.length - 1
            const hasMenu = hasMenuContent(category)
            const isMenuTrigger = hasMenu && !isMobile
            return (
              <CarouselItem key={category.id} css={isLast ? lastItemClass : itemClass}>
                <NavItem
                  itemId={category.id}
                  data-testid={TEST_LOCATORS.NAV.NAV_MENU_ITEM(category.label)}
                  href={isMenuTrigger ? undefined : category.href}
                  onClick={
                    onCategoryLinkClick
                      ? () => {
                          onCategoryLinkClick(category, index)
                        }
                      : undefined
                  }
                  variant={category.highlight ? 'cantaloupe' : 'default'}
                  size="md"
                  triggerRef={isMenuTrigger && isActive ? setTriggerEl : undefined}
                  onPointerEnter={isMobile ? undefined : handlePointerEnter}
                  onFocus={isMobile ? undefined : handleFocus}
                  onBlur={onCategoryLeave}
                  onKeyDown={isMenuTrigger ? handleKeyDown : undefined}
                  aria-expanded={isMenuTrigger ? isActive : undefined}
                  aria-controls={isMenuTrigger && isActive ? buildMenuPanelId(category.id) : undefined}
                >
                  {category.label}
                </NavItem>
              </CarouselItem>
            )
          })}
        </CarouselContent>
      </Carousel>
    </SectionContentContainer>
  )
}
